import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom'
import { useEffect } from 'react'
import i18n from './i18n/i18n'

import Home from './Home'
import SignUp from './SignUpPage'
import { GrowthBook } from '@growthbook/growthbook'
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import useGTM from './hooks/useGtm'
import { GrowthBookProvider as CustomGrowthBookProvider } from './contexts/GrowthBookContext'

const supported = ['en', 'fr', 'de', 'es', 'it', 'ja']

const browserLang = (() => {
  const lang = navigator.language.split('-')[0]
  return supported.includes(lang) ? lang : 'en'
})()

function LanguageWrapper() {
  useGTM()

  const { lang } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!lang) {
      i18n.changeLanguage(browserLang)
      return
    }

    if (supported.includes(lang)) {
      i18n.changeLanguage(lang)
      if (lang === browserLang && location.pathname.startsWith(`/${browserLang}`)) {
        const cleanPath = location.pathname.replace(new RegExp(`^/${browserLang}`), '') || '/'
        navigate(cleanPath, { replace: true })
      }
      if (lang === 'en' && browserLang === 'en' && location.pathname.startsWith('/en')) {
        const cleanPath = location.pathname.replace(/^\/en/, '') || '/'
        navigate(cleanPath, { replace: true })
      }
    } else {
      navigate('/', { replace: true })
    }
  }, [lang, location.pathname, navigate])

  return (
    <>
      <Outlet />
    </>
  )
}

export default function Root() {
  const GB_BASE_URL = process.env.REACT_APP_GB_API
  const GB_BASE_KEY = process.env.REACT_APP_GB_KEY

  const growthbook = new GrowthBook({
    apiHost: GB_BASE_URL || 'https://cdn.growthbook.io',
    clientKey: GB_BASE_KEY || '',
    enableDevMode: process.env.NODE_ENV === 'development',
    trackingCallback: (experiment, result) => {
      console.log('Viewed Experiment', {
        experimentId: experiment.key,
        variationId: result.key,
      })
    },
    plugins: [autoAttributesPlugin()],
  })

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const canRedirect = urlParams.get('redirect') !== 'false'

    const inProgress = localStorage.getItem('googleAuthInProgress')
    const startTime = parseInt(localStorage.getItem('googleAuthStartTime'), 10)

    if (inProgress && startTime) {
      const timeElapsed = Date.now() - startTime

      if (timeElapsed < 3 * 60 * 1000) {
        localStorage.setItem('googleAuthCancelled', 'true')
      } else {
        localStorage.setItem('isCompleted', true)
        localStorage.removeItem('googleAuthCancelled')
      }

      localStorage.removeItem('googleAuthInProgress')
      localStorage.removeItem('googleAuthStartTime')
    }

    const isCompleted = localStorage.getItem('isCompleted')
    if (isCompleted && canRedirect) {
      const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL
      window.location.href = `${SWIPEY_BASE_URL}/paywall?from_landing=true`
    }
  }, [])

  function getUTMParams() {
    const params = new URLSearchParams(window.location.search);

    const utm = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      cmpid: params.get('cmpid'),
      pubid: params.get('pubid'),
      prelandername: params.get('prelandername'),
    };

    const hasUtm = Object.values(utm).some(Boolean);

    if (hasUtm) {
      // localStorage.setItem('utm_params', JSON.stringify(utm));
      return utm;
    }

    const stored = localStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  }

  useEffect(() => {
    growthbook.init({ streaming: true }).then(() => {
      const deviceId = Math.random().toString(36).substring(2);
      const utm = getUTMParams();
      console.log('utm', utm);
      growthbook.setAttributes({
        id: deviceId,
        deviceId: deviceId,
        utmSource: utm.utm_source || null,
        utmMedium: utm.utm_medium || null,
        utmCampaign: utm.utm_campaign || null,
        cmpid: utm.cmpid || null,
        pubid: utm.pubid || null,
        prelandername: utm.prelandername || null,
      });
    });
  }, []);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <CustomGrowthBookProvider>
        <Router>
          <Routes>
            <Route path="/" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignUp />} />
              <Route path="newaccount" element={<Home />} />
              <Route path="pw" element={<Home />} />
            </Route>

            <Route path="/halloween" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignUp />} />
            </Route>

            <Route path="/:lang" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignUp />} />
              <Route path="newaccount" element={<Home />} />
              <Route path="pw" element={<Home />} />
            </Route>

            <Route path="/:lang/halloween" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignUp />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </CustomGrowthBookProvider>
    </GrowthBookProvider>
  )
}
